/**
 * Persisted draft for the online claims funnel (browser {@link sessionStorage}).
 * Multi-vehicle driver DOB / licence / years are stored under {@link CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY},
 * not inside the main {@link CLAIM_FLOW_DRAFT_STORAGE_KEY} blob; {@link readClaimDraft} merges them for callers.
 * @module claims/claimDraftStorage
 */

import {
  isDdMmYyyyInLocalRange,
  isValidDdMmYyyyCalendarDate,
  parseDdMmYyyyToDate,
  PORTAL_DATE_MIN,
  startOfTodayLocal,
} from '../utils/dobDateInput'
import { LEGACY_OTHER_CLAIM_SUBTYPE_HIT_OBJECT_OR_ANIMAL_ID, OTHER_CLAIM_SUBTYPE_OPTIONS } from './otherClaimSubtypeOptions'
import { migrateLegacyMultiVehicleUnattendedDraft } from './multiVehicleDriverDraft'
import { MULTI_VEHICLE_OTHER_PARTY_STRING_KEYS } from './multiVehicleOtherPartyDrivers'
import { sanitizeClaimDraftFreeTextFields } from './sanitizeClaimDraftFreeText'

/** What the vehicle collided with (single-vehicle claim branch). */
export type SingleVehicleCollisionTargetId =
  | 'animal'
  | 'object'
  | 'third_party_property'
  | 'other'

/** sessionStorage key for in-progress online claim draft (towing, etc.). */
export const CLAIM_FLOW_DRAFT_STORAGE_KEY = 'club4x4_claim_flow_draft'

/**
 * sessionStorage key for multi-vehicle driver licence / DOB slice (kept out of {@link CLAIM_FLOW_DRAFT_STORAGE_KEY}).
 * Still tab-scoped like the main draft; not sent server-side by this module.
 */
export const CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY = 'club4x4_claim_flow_mv_driver_pii'

/** Snapshot merged into storage during the online claims funnel (towing, windscreen text, etc.). */
export interface ClaimFlowDraft {
  policyRef?: string | null
  /** Whether the vehicle requires towing (after user answers on `/claims/towing`). */
  requiresTowing?: boolean | null
  /** Windscreen/glass branch: free-text description (max 1000 chars in UI). */
  windscreenDescription?: string | null
  /** Windscreen/glass: whether damage is a small chip (yes/no). */
  windscreenSmallChip?: boolean | null
  /**
   * Whether the windscreen was already repaired/replaced before this claim (any chip vs crack path).
   * When true, user provides who completed the work on the further-detail step and the repair-option step is skipped.
   */
  windscreenReplacedAlready?: boolean | null
  /** Windscreen/glass: repair network vs own repairer. */
  windscreenRepairOption?: 'trusted' | 'own' | null
  windscreenRepairerCompany?: string | null
  windscreenRepairerEmail?: string | null
  windscreenRepairerPhone?: string | null
  /** Legacy draft field; attachments are not collected in the portal. */
  windscreenAttachmentFileNames?: string[] | null
  /** Windscreen branch: registered for GST (before describe step). */
  windscreenGstRegistered?: boolean | null
  /** Windscreen branch: legacy field; no longer written by the portal (always cleared on GST save). */
  windscreenAbn?: string | null
  /** Windscreen branch: entitled to claim input tax credit (only when registered for GST). */
  windscreenItcEntitled?: boolean | null
  /** Windscreen branch: ITC percentage 0–100 when entitled to claim ITC. */
  windscreenItcPercentage?: number | null
  /**
   * Non–windscreen: stable id matching {@link OTHER_CLAIM_SUBTYPE_OPTIONS} (mirrors URL
   * `claimSubtype`; used when query is restored from draft).
   */
  otherClaimSubtypeId?: string | null
  /** Multi-vehicle collision: vehicle attended at time of collision. */
  multiVehicleAttendedAtCollision?: boolean | null
  /** Multi-vehicle: primary driver was driving (only when attended). */
  multiVehiclePrimaryDriverDriving?: boolean | null
  /** Multi-vehicle: a secondary listed driver was driving (when primary was not). */
  multiVehicleSecondaryOnPolicyDriving?: boolean | null
  /** Multi-vehicle: chosen listed driver id when {@link multiVehicleSecondaryOnPolicyDriving} is true. */
  multiVehicleListedDriverId?: string | null
  /**
   * Multi-vehicle: how driver details were captured before “further details”.
   * `none_unattended` when the vehicle was not attended.
   */
  multiVehicleDriverCaptureMode?: 'none_unattended' | 'primary' | 'listed' | 'manual' | null
  multiVehicleDriverFirstName?: string | null
  multiVehicleDriverLastName?: string | null
  /** Persisted in {@link CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY}; merged by {@link readClaimDraft}. */
  multiVehicleDriverDobDdMmYyyy?: string | null
  multiVehicleDriverGender?: 'Male' | 'Female' | null
  /** Persisted in {@link CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY}; merged by {@link readClaimDraft}. */
  multiVehicleLicenseNumber?: string | null
  /** Persisted in {@link CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY}; merged by {@link readClaimDraft}. */
  multiVehicleLicenseExpiryDdMmYyyy?: string | null
  /** Whole years the driver has held a licence; stored in {@link CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY}. */
  multiVehicleYearsLicensed?: number | null
  /** Multi-vehicle: whether the user has details for the other involved driver (optional fields follow when true). */
  multiVehicleOtherPartyDetailsKnown?: boolean | null
  multiVehicleOtherPartyName?: string | null
  multiVehicleOtherPartyAddress?: string | null
  multiVehicleOtherPartyDobDdMmYyyy?: string | null
  multiVehicleOtherPartyPhone?: string | null
  multiVehicleOtherPartyRegistration?: string | null
  multiVehicleOtherPartyLicenceNumber?: string | null
  multiVehicleOtherParty2Name?: string | null
  multiVehicleOtherParty2Address?: string | null
  multiVehicleOtherParty2Phone?: string | null
  multiVehicleOtherParty2Registration?: string | null
  multiVehicleOtherParty3Name?: string | null
  multiVehicleOtherParty3Address?: string | null
  multiVehicleOtherParty3Phone?: string | null
  multiVehicleOtherParty3Registration?: string | null
  /**
   * Multi-vehicle collision: legacy single narrative (pre–split describe step). Copied into
   * {@link multiVehicleIncidentDescription} on {@link readClaimDraft} when incident is empty; cleared when saving describe.
   */
  multiVehicleClaimDescription?: string | null
  /**
   * Weather claims: free-text description of weather conditions at the time of loss (max 1000 chars in UI).
   */
  weatherClaimConditionsDescription?: string | null
  /**
   * Fire claims: what type of fire it was (e.g. bushfire, grass fire, electrical, engine bay) — max 1000 chars in UI.
   */
  fireClaimTypeDescription?: string | null
  /** Fire claims only: was the insured vehicle parked when the loss occurred (driver step pre-screen). */
  fireVehicleParkedAtIncident?: boolean | null
  /** @deprecated Legacy draft field; no longer collected in the portal. */
  fireVehicleStillParkedAtLocation?: boolean | null
  /** Weather claims only: was the insured vehicle parked when the loss occurred (driver step pre-screen). */
  weatherVehicleParkedAtIncident?: boolean | null
  /** @deprecated Legacy draft field; no longer collected in the portal. */
  weatherVehicleStillParkedAtLocation?: boolean | null
  /**
   * Theft / break-in claims: second narrative field (theft vs break-in, forced entry, property taken, etc.) —
   * max 1000 chars in UI.
   */
  theftOrBreakInDetailDescription?: string | null
  /** Theft claims only: was the insured vehicle parked when the loss occurred (driver step pre-screen). */
  theftVehicleParkedAtIncident?: boolean | null
  /** Theft claims only: was the whole vehicle stolen (driver step pre-screen). */
  theftVehicleStolenAtIncident?: boolean | null
  /** Theft claims only: was anything stolen from the vehicle when it was not stolen (driver step). */
  theftItemsStolenFromVehicle?: boolean | null
  /**
   * @deprecated Use {@link theftVehicleStolenAtIncident} and {@link theftItemsStolenFromVehicle}.
   * Legacy combined “something stolen or vehicle stolen” answer.
   */
  theftSomethingStolenAtIncident?: boolean | null
  /** @deprecated Legacy draft field; no longer collected in the portal. */
  theftVehicleStillParkedAtLocation?: boolean | null
  /**
   * Multi-vehicle collision: how the incident occurred (max 1000 chars in UI).
   * When empty in storage but {@link multiVehicleClaimDescription} is set, {@link readClaimDraft} copies the
   * legacy text into this field so raw draft readers stay aligned with {@link getMultiVehicleDescribeParts}.
   */
  multiVehicleIncidentDescription?: string | null
  /**
   * Multi-vehicle collision: damage and impact — vehicles, property, drivability (max 1000 chars in UI).
   */
  multiVehicleDamageDescription?: string | null
  /**
   * Single-vehicle claim: what the insured vehicle collided with (after driver-at-collision step).
   * When {@link singleVehicleCollisionTarget} is `other`, {@link singleVehicleCollisionTargetOtherText} must be non-empty.
   */
  singleVehicleCollisionTarget?: SingleVehicleCollisionTargetId | null
  /** Free-text when {@link singleVehicleCollisionTarget} is `other` (max 500 chars in UI). */
  singleVehicleCollisionTargetOtherText?: string | null
  /** Single-vehicle collision: how the incident occurred (max 1000 chars in UI). */
  singleVehicleIncidentDescription?: string | null
  /** Single-vehicle collision: damage and impact (max 1000 chars in UI). */
  singleVehicleDamageDescription?: string | null
  /** Incident date as entered (DD/MM/YYYY). */
  incidentDateDdMmYyyy?: string | null
  /** Incident time as HH:MM (12-hour clock; paired with {@link incidentTimeAmPm}). */
  incidentTimeHm?: string | null
  incidentTimeAmPm?: 'AM' | 'PM' | null
  /** Free-text rough location when {@link incidentAddressUnknown} is true. */
  incidentAddressSearch?: string | null
  incidentStreet?: string | null
  incidentSuburb?: string | null
  incidentPostcode?: string | null
  incidentState?: string | null
  /** When true, street/suburb/postcode are optional; state is still required in the UI for unknown-address paths. */
  incidentAddressUnknown?: boolean | null
  updatedAt: string
}

const ALLOWED_OTHER_SUBTYPE_IDS = new Set<string>(OTHER_CLAIM_SUBTYPE_OPTIONS.map((o) => o.id))

/**
 * Rewrites removed hit-object subtype to `single-vehicle` so legacy session drafts still validate.
 * @returns Parsed shape (possibly updated) and whether storage should be rewritten.
 */
function normalizeLegacyOtherClaimSubtypeDraftShape(parsed: unknown): { value: unknown; didRewriteSubtype: boolean } {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { value: parsed, didRewriteSubtype: false }
  }
  const o = parsed as Record<string, unknown>
  if (o.otherClaimSubtypeId === LEGACY_OTHER_CLAIM_SUBTYPE_HIT_OBJECT_OR_ANIMAL_ID) {
    return { value: { ...o, otherClaimSubtypeId: 'single-vehicle' }, didRewriteSubtype: true }
  }
  return { value: parsed, didRewriteSubtype: false }
}

/** Removes the draft key (e.g. malformed JSON or invalid shape). Swallows storage errors. */
function clearDraftStorage(): void {
  try {
    sessionStorage.removeItem(CLAIM_FLOW_DRAFT_STORAGE_KEY)
    sessionStorage.removeItem(CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY)
  } catch {
    // Private mode / quota — ignore
  }
}

function stripMvDriverPiiFieldsForMainStorage(draft: ClaimFlowDraft): ClaimFlowDraft {
  const {
    multiVehicleDriverDobDdMmYyyy: _dob,
    multiVehicleLicenseNumber: _lic,
    multiVehicleLicenseExpiryDdMmYyyy: _exp,
    multiVehicleYearsLicensed: _years,
    ...rest
  } = draft
  return rest as ClaimFlowDraft
}

function mainBlobContainsMvDriverPii(d: ClaimFlowDraft): boolean {
  if (d.multiVehicleDriverDobDdMmYyyy != null && String(d.multiVehicleDriverDobDdMmYyyy).trim() !== '') {
    return true
  }
  if (d.multiVehicleLicenseNumber != null && String(d.multiVehicleLicenseNumber).trim() !== '') {
    return true
  }
  if (d.multiVehicleLicenseExpiryDdMmYyyy != null && String(d.multiVehicleLicenseExpiryDdMmYyyy).trim() !== '') {
    return true
  }
  if (d.multiVehicleYearsLicensed != null && Number.isInteger(d.multiVehicleYearsLicensed)) {
    return true
  }
  return false
}

function readMvDriverPiiRecord(): Partial<
  Pick<
    ClaimFlowDraft,
    | 'multiVehicleDriverDobDdMmYyyy'
    | 'multiVehicleLicenseNumber'
    | 'multiVehicleLicenseExpiryDdMmYyyy'
    | 'multiVehicleYearsLicensed'
  >
> | null {
  try {
    const raw = sessionStorage.getItem(CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY)
    if (!raw) return null
    const p: unknown = JSON.parse(raw)
    if (typeof p !== 'object' || p === null) return null
    const o = p as Record<string, unknown>
    const out: Partial<
      Pick<
        ClaimFlowDraft,
        | 'multiVehicleDriverDobDdMmYyyy'
        | 'multiVehicleLicenseNumber'
        | 'multiVehicleLicenseExpiryDdMmYyyy'
        | 'multiVehicleYearsLicensed'
      >
    > = {}
    if ('multiVehicleDriverDobDdMmYyyy' in o) {
      const v = o.multiVehicleDriverDobDdMmYyyy
      if (v === null) out.multiVehicleDriverDobDdMmYyyy = null
      else if (typeof v === 'string') out.multiVehicleDriverDobDdMmYyyy = v
    }
    if ('multiVehicleLicenseNumber' in o) {
      const v = o.multiVehicleLicenseNumber
      if (v === null) out.multiVehicleLicenseNumber = null
      else if (typeof v === 'string') out.multiVehicleLicenseNumber = v
    }
    if ('multiVehicleLicenseExpiryDdMmYyyy' in o) {
      const v = o.multiVehicleLicenseExpiryDdMmYyyy
      if (v === null) out.multiVehicleLicenseExpiryDdMmYyyy = null
      else if (typeof v === 'string') out.multiVehicleLicenseExpiryDdMmYyyy = v
    }
    if ('multiVehicleYearsLicensed' in o) {
      const v = o.multiVehicleYearsLicensed
      if (v === null) out.multiVehicleYearsLicensed = null
      else if (typeof v === 'number' && Number.isInteger(v)) out.multiVehicleYearsLicensed = v
    }
    return Object.keys(out).length > 0 ? out : null
  } catch {
    return null
  }
}

function coalesceMvDriverStringField(
  mainVal: string | null | undefined,
  piiVal: string | null | undefined
): string | null | undefined {
  if (piiVal != null && String(piiVal).trim() !== '') return piiVal
  if (mainVal != null && String(mainVal).trim() !== '') return mainVal
  return piiVal !== undefined ? piiVal : mainVal
}

function coalesceMvDriverYearsField(
  mainVal: number | null | undefined,
  piiVal: number | null | undefined
): number | null | undefined {
  if (typeof piiVal === 'number' && Number.isInteger(piiVal)) return piiVal
  if (typeof mainVal === 'number' && Number.isInteger(mainVal)) return mainVal
  return piiVal !== undefined ? piiVal : mainVal
}

function mergeMvPiiRecordIntoDraft(
  main: ClaimFlowDraft,
  pii: NonNullable<ReturnType<typeof readMvDriverPiiRecord>>
): ClaimFlowDraft {
  return {
    ...main,
    ...(pii.multiVehicleDriverDobDdMmYyyy !== undefined
      ? {
          multiVehicleDriverDobDdMmYyyy: coalesceMvDriverStringField(
            main.multiVehicleDriverDobDdMmYyyy,
            pii.multiVehicleDriverDobDdMmYyyy
          ),
        }
      : {}),
    ...(pii.multiVehicleLicenseNumber !== undefined
      ? {
          multiVehicleLicenseNumber: coalesceMvDriverStringField(
            main.multiVehicleLicenseNumber,
            pii.multiVehicleLicenseNumber
          ),
        }
      : {}),
    ...(pii.multiVehicleLicenseExpiryDdMmYyyy !== undefined
      ? {
          multiVehicleLicenseExpiryDdMmYyyy: coalesceMvDriverStringField(
            main.multiVehicleLicenseExpiryDdMmYyyy,
            pii.multiVehicleLicenseExpiryDdMmYyyy
          ),
        }
      : {}),
    ...(pii.multiVehicleYearsLicensed !== undefined
      ? {
          multiVehicleYearsLicensed: coalesceMvDriverYearsField(
            main.multiVehicleYearsLicensed,
            pii.multiVehicleYearsLicensed
          ),
        }
      : {}),
  }
}

/** Driver DOB / licence fields for claim submit (PII slice preferred over empty main-blob placeholders). */
export type MvDriverLicenceFieldsForSubmit = Pick<
  ClaimFlowDraft,
  | 'multiVehicleDriverDobDdMmYyyy'
  | 'multiVehicleLicenseNumber'
  | 'multiVehicleLicenseExpiryDdMmYyyy'
  | 'multiVehicleYearsLicensed'
>

/**
 * Resolves MV driver PII for POST bodies (fire, weather, theft, etc.). Reads the dedicated PII slice
 * and prefers non-empty values over empty placeholders on the main draft blob.
 */
export function pickMvDriverLicenceFieldsForClaimSubmit(
  draft: ClaimFlowDraft
): MvDriverLicenceFieldsForSubmit {
  const pii = readMvDriverPiiRecord()
  return {
    multiVehicleDriverDobDdMmYyyy: coalesceMvDriverStringField(
      draft.multiVehicleDriverDobDdMmYyyy,
      pii?.multiVehicleDriverDobDdMmYyyy
    ) as string | null | undefined,
    multiVehicleLicenseNumber: coalesceMvDriverStringField(
      draft.multiVehicleLicenseNumber,
      pii?.multiVehicleLicenseNumber
    ) as string | null | undefined,
    multiVehicleLicenseExpiryDdMmYyyy: coalesceMvDriverStringField(
      draft.multiVehicleLicenseExpiryDdMmYyyy,
      pii?.multiVehicleLicenseExpiryDdMmYyyy
    ) as string | null | undefined,
    multiVehicleYearsLicensed: coalesceMvDriverYearsField(
      draft.multiVehicleYearsLicensed,
      pii?.multiVehicleYearsLicensed
    ) as number | null | undefined,
  }
}

function persistMvDriverPiiSlice(draft: ClaimFlowDraft): void {
  try {
    const dob = draft.multiVehicleDriverDobDdMmYyyy
    const lic = draft.multiVehicleLicenseNumber
    const exp = draft.multiVehicleLicenseExpiryDdMmYyyy
    const years = draft.multiVehicleYearsLicensed

    const hasDob = dob != null && String(dob).trim() !== ''
    const hasLic = lic != null && String(lic).trim() !== ''
    const hasExp = exp != null && String(exp).trim() !== ''
    const hasYears = years != null && Number.isInteger(years)

    if (!hasDob && !hasLic && !hasExp && !hasYears) {
      sessionStorage.removeItem(CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY)
      return
    }

    const rec = sanitizeClaimDraftFreeTextFields({
      multiVehicleDriverDobDdMmYyyy: hasDob ? String(dob).trim() : null,
      multiVehicleLicenseNumber: hasLic ? String(lic).trim() : null,
      multiVehicleLicenseExpiryDdMmYyyy: hasExp ? String(exp).trim() : null,
      multiVehicleYearsLicensed: hasYears ? years : null,
    })
    sessionStorage.setItem(CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY, JSON.stringify(rec))
  } catch {
    // Private mode / quota — ignore
  }
}

/**
 * Migrates legacy drafts that stored MV driver PII inside the main blob into {@link CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY}.
 */
function migrateLegacyMvDriverPiiOutOfMainIfPresent(main: ClaimFlowDraft): ClaimFlowDraft {
  if (!mainBlobContainsMvDriverPii(main)) return main
  try {
    persistMvDriverPiiSlice(main)
    const stripped = stripMvDriverPiiFieldsForMainStorage(main)
    sessionStorage.setItem(CLAIM_FLOW_DRAFT_STORAGE_KEY, JSON.stringify(stripped))
    return stripped
  } catch (err) {
    console.error('Failed to migrate MV driver PII out of main draft', err)
    return main
  }
}

const MV_PII_KEYS_FOR_SLICE_WRITE = [
  'multiVehicleDriverDobDdMmYyyy',
  'multiVehicleLicenseNumber',
  'multiVehicleLicenseExpiryDdMmYyyy',
  'multiVehicleYearsLicensed',
] as const satisfies readonly (keyof ClaimFlowDraft)[]

function mvDriverPiiFieldHasValue(
  rec: Record<string, unknown>,
  key: (typeof MV_PII_KEYS_FOR_SLICE_WRITE)[number]
): boolean {
  const v = rec[key]
  if (key === 'multiVehicleYearsLicensed') {
    return typeof v === 'number' && Number.isInteger(v)
  }
  return v != null && String(v).trim() !== ''
}

/**
 * Main session blob omits MV driver DOB / licence (see {@link stripMvDriverPiiFieldsForMainStorage}).
 * Merge-only writes from {@link readDraftStorageRaw} therefore lack those keys; without this merge,
 * {@link persistMvDriverPiiSlice} would treat the draft as empty and remove the PII slice — breaking
 * gates after “what happened” and leaving licence fields blank when returning to driver.
 */
function mergeStoredMvDriverPiiIntoDraftForSliceWrite(
  incoming: ClaimFlowDraft | Record<string, unknown>
): ClaimFlowDraft {
  const rec = incoming as Record<string, unknown>
  const existing = readMvDriverPiiRecord()
  if (!existing) {
    return incoming as ClaimFlowDraft
  }
  const out: Record<string, unknown> = { ...rec }
  for (const k of MV_PII_KEYS_FOR_SLICE_WRITE) {
    if (mvDriverPiiFieldHasValue(rec, k)) {
      continue
    }
    if (existing[k] !== undefined) {
      out[k] = existing[k]
    }
  }
  return out as unknown as ClaimFlowDraft
}

/**
 * Persists the main draft blob. Accepts {@link ClaimFlowDraft} or a merge payload from
 * {@link readDraftStorageRaw} (may not pass {@link isClaimFlowDraft} until the user completes fields).
 */
function writeClaimFlowDraftToStorage(draft: ClaimFlowDraft | Record<string, unknown>): void {
  try {
    const merged = sanitizeClaimDraftFreeTextFields(draft) as ClaimFlowDraft
    const mergedForPiiSlice = mergeStoredMvDriverPiiIntoDraftForSliceWrite(merged)
    persistMvDriverPiiSlice(mergedForPiiSlice)
    const mainOnly = stripMvDriverPiiFieldsForMainStorage(merged)
    sessionStorage.setItem(CLAIM_FLOW_DRAFT_STORAGE_KEY, JSON.stringify(mainOnly))
  } catch (err) {
    console.error('Failed to persist claim flow draft', err)
  }
}

/**
 * Raw object from sessionStorage for merge-only writes. Does not validate or clear.
 *
 * {@link readClaimDraft} can return null while storage still holds a recoverable object (e.g. brief
 * type drift). Persist helpers must merge from this instead of `readClaimDraft()`, because the
 * latter used to {@link clearDraftStorage} on validation failure and the next `...prev` spread with
 * `prev === null` would overwrite the draft with only the latest field — dropping `incidentDateDdMmYyyy`
 * and causing windscreen FNOL 400s.
 */
function readDraftStorageRaw(): Record<string, unknown> {
  try {
    const raw = sessionStorage.getItem(CLAIM_FLOW_DRAFT_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    // Ignore; caller will write a merged payload
  }
  return {}
}

/**
 * Runtime validation for JSON restored from sessionStorage.
 * Requires `updatedAt` (non-empty string); optional fields must match expected types when present.
 */
export function isClaimFlowDraft(obj: unknown): obj is ClaimFlowDraft {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return false
  const o = obj as Record<string, unknown>
  if (typeof o.updatedAt !== 'string' || o.updatedAt.length === 0) return false
  if (o.policyRef !== undefined && o.policyRef !== null && typeof o.policyRef !== 'string') {
    return false
  }
  if (
    o.requiresTowing !== undefined &&
    o.requiresTowing !== null &&
    typeof o.requiresTowing !== 'boolean'
  ) {
    return false
  }
  if (
    o.windscreenDescription !== undefined &&
    o.windscreenDescription !== null &&
    typeof o.windscreenDescription !== 'string'
  ) {
    return false
  }
  if (
    o.windscreenSmallChip !== undefined &&
    o.windscreenSmallChip !== null &&
    typeof o.windscreenSmallChip !== 'boolean'
  ) {
    return false
  }
  if (
    o.windscreenReplacedAlready !== undefined &&
    o.windscreenReplacedAlready !== null &&
    typeof o.windscreenReplacedAlready !== 'boolean'
  ) {
    return false
  }
  if (
    o.windscreenRepairOption !== undefined &&
    o.windscreenRepairOption !== null &&
    o.windscreenRepairOption !== 'trusted' &&
    o.windscreenRepairOption !== 'own'
  ) {
    return false
  }
  for (const key of ['windscreenRepairerCompany', 'windscreenRepairerEmail', 'windscreenRepairerPhone'] as const) {
    if (o[key] !== undefined && o[key] !== null && typeof o[key] !== 'string') {
      return false
    }
  }
  if (o.windscreenAttachmentFileNames !== undefined && o.windscreenAttachmentFileNames !== null) {
    if (!Array.isArray(o.windscreenAttachmentFileNames)) return false
    if (!o.windscreenAttachmentFileNames.every((x) => typeof x === 'string')) return false
  }
  if (
    o.windscreenGstRegistered !== undefined &&
    o.windscreenGstRegistered !== null &&
    typeof o.windscreenGstRegistered !== 'boolean'
  ) {
    return false
  }
  if (o.windscreenAbn !== undefined && o.windscreenAbn !== null && typeof o.windscreenAbn !== 'string') {
    return false
  }
  if (
    o.windscreenItcEntitled !== undefined &&
    o.windscreenItcEntitled !== null &&
    typeof o.windscreenItcEntitled !== 'boolean'
  ) {
    return false
  }
  if (o.windscreenItcPercentage !== undefined && o.windscreenItcPercentage !== null) {
    if (typeof o.windscreenItcPercentage !== 'number' || !Number.isInteger(o.windscreenItcPercentage)) {
      return false
    }
    if (o.windscreenItcPercentage < 0 || o.windscreenItcPercentage > 100) {
      return false
    }
  }
  const incidentStrKeys = [
    'incidentDateDdMmYyyy',
    'incidentTimeHm',
    'incidentAddressSearch',
    'incidentStreet',
    'incidentSuburb',
    'incidentPostcode',
    'incidentState',
  ] as const
  for (const key of incidentStrKeys) {
    if (o[key] !== undefined && o[key] !== null && typeof o[key] !== 'string') {
      return false
    }
  }
  if (
    o.incidentTimeAmPm !== undefined &&
    o.incidentTimeAmPm !== null &&
    o.incidentTimeAmPm !== 'AM' &&
    o.incidentTimeAmPm !== 'PM'
  ) {
    return false
  }
  if (
    o.incidentAddressUnknown !== undefined &&
    o.incidentAddressUnknown !== null &&
    typeof o.incidentAddressUnknown !== 'boolean'
  ) {
    return false
  }
  if (
    o.otherClaimSubtypeId !== undefined &&
    o.otherClaimSubtypeId !== null &&
    (typeof o.otherClaimSubtypeId !== 'string' || !ALLOWED_OTHER_SUBTYPE_IDS.has(o.otherClaimSubtypeId))
  ) {
    return false
  }
  for (const key of [
    'multiVehicleDriverFirstName',
    'multiVehicleDriverLastName',
    'multiVehicleDriverDobDdMmYyyy',
    'multiVehicleLicenseNumber',
    'multiVehicleLicenseExpiryDdMmYyyy',
  ] as const) {
    if (o[key] !== undefined && o[key] !== null && typeof o[key] !== 'string') {
      return false
    }
  }
  if (
    o.multiVehicleAttendedAtCollision !== undefined &&
    o.multiVehicleAttendedAtCollision !== null &&
    typeof o.multiVehicleAttendedAtCollision !== 'boolean'
  ) {
    return false
  }
  if (
    o.multiVehiclePrimaryDriverDriving !== undefined &&
    o.multiVehiclePrimaryDriverDriving !== null &&
    typeof o.multiVehiclePrimaryDriverDriving !== 'boolean'
  ) {
    return false
  }
  if (
    o.multiVehicleSecondaryOnPolicyDriving !== undefined &&
    o.multiVehicleSecondaryOnPolicyDriving !== null &&
    typeof o.multiVehicleSecondaryOnPolicyDriving !== 'boolean'
  ) {
    return false
  }
  if (
    o.multiVehicleListedDriverId !== undefined &&
    o.multiVehicleListedDriverId !== null &&
    typeof o.multiVehicleListedDriverId !== 'string'
  ) {
    return false
  }
  if (o.multiVehicleDriverCaptureMode !== undefined && o.multiVehicleDriverCaptureMode !== null) {
    const m = o.multiVehicleDriverCaptureMode
    if (m !== 'none_unattended' && m !== 'primary' && m !== 'listed' && m !== 'manual') {
      return false
    }
  }
  if (
    o.multiVehicleDriverGender !== undefined &&
    o.multiVehicleDriverGender !== null &&
    o.multiVehicleDriverGender !== 'Male' &&
    o.multiVehicleDriverGender !== 'Female'
  ) {
    return false
  }
  if (o.multiVehicleYearsLicensed !== undefined && o.multiVehicleYearsLicensed !== null) {
    if (typeof o.multiVehicleYearsLicensed !== 'number' || !Number.isInteger(o.multiVehicleYearsLicensed)) {
      return false
    }
    if (o.multiVehicleYearsLicensed < 1 || o.multiVehicleYearsLicensed > 100) {
      return false
    }
  }
  if (
    o.multiVehicleOtherPartyDetailsKnown !== undefined &&
    o.multiVehicleOtherPartyDetailsKnown !== null &&
    typeof o.multiVehicleOtherPartyDetailsKnown !== 'boolean'
  ) {
    return false
  }
  for (const key of MULTI_VEHICLE_OTHER_PARTY_STRING_KEYS) {
    if (o[key] !== undefined && o[key] !== null && typeof o[key] !== 'string') {
      return false
    }
  }
  if (
    o.multiVehicleClaimDescription !== undefined &&
    o.multiVehicleClaimDescription !== null &&
    typeof o.multiVehicleClaimDescription !== 'string'
  ) {
    return false
  }
  if (
    o.weatherClaimConditionsDescription !== undefined &&
    o.weatherClaimConditionsDescription !== null &&
    typeof o.weatherClaimConditionsDescription !== 'string'
  ) {
    return false
  }
  if (
    o.fireClaimTypeDescription !== undefined &&
    o.fireClaimTypeDescription !== null &&
    typeof o.fireClaimTypeDescription !== 'string'
  ) {
    return false
  }
  if (
    o.fireVehicleParkedAtIncident !== undefined &&
    o.fireVehicleParkedAtIncident !== null &&
    typeof o.fireVehicleParkedAtIncident !== 'boolean'
  ) {
    return false
  }
  if (
    o.fireVehicleStillParkedAtLocation !== undefined &&
    o.fireVehicleStillParkedAtLocation !== null &&
    typeof o.fireVehicleStillParkedAtLocation !== 'boolean'
  ) {
    return false
  }
  if (
    o.weatherVehicleParkedAtIncident !== undefined &&
    o.weatherVehicleParkedAtIncident !== null &&
    typeof o.weatherVehicleParkedAtIncident !== 'boolean'
  ) {
    return false
  }
  if (
    o.weatherVehicleStillParkedAtLocation !== undefined &&
    o.weatherVehicleStillParkedAtLocation !== null &&
    typeof o.weatherVehicleStillParkedAtLocation !== 'boolean'
  ) {
    return false
  }
  if (
    o.theftOrBreakInDetailDescription !== undefined &&
    o.theftOrBreakInDetailDescription !== null &&
    typeof o.theftOrBreakInDetailDescription !== 'string'
  ) {
    return false
  }
  if (
    o.theftVehicleParkedAtIncident !== undefined &&
    o.theftVehicleParkedAtIncident !== null &&
    typeof o.theftVehicleParkedAtIncident !== 'boolean'
  ) {
    return false
  }
  if (
    o.theftVehicleStolenAtIncident !== undefined &&
    o.theftVehicleStolenAtIncident !== null &&
    typeof o.theftVehicleStolenAtIncident !== 'boolean'
  ) {
    return false
  }
  if (
    o.theftItemsStolenFromVehicle !== undefined &&
    o.theftItemsStolenFromVehicle !== null &&
    typeof o.theftItemsStolenFromVehicle !== 'boolean'
  ) {
    return false
  }
  if (
    o.theftSomethingStolenAtIncident !== undefined &&
    o.theftSomethingStolenAtIncident !== null &&
    typeof o.theftSomethingStolenAtIncident !== 'boolean'
  ) {
    return false
  }
  if (
    o.theftVehicleStillParkedAtLocation !== undefined &&
    o.theftVehicleStillParkedAtLocation !== null &&
    typeof o.theftVehicleStillParkedAtLocation !== 'boolean'
  ) {
    return false
  }
  if (
    o.multiVehicleIncidentDescription !== undefined &&
    o.multiVehicleIncidentDescription !== null &&
    typeof o.multiVehicleIncidentDescription !== 'string'
  ) {
    return false
  }
  if (
    o.multiVehicleDamageDescription !== undefined &&
    o.multiVehicleDamageDescription !== null &&
    typeof o.multiVehicleDamageDescription !== 'string'
  ) {
    return false
  }
  if (o.singleVehicleCollisionTarget !== undefined && o.singleVehicleCollisionTarget !== null) {
    if (!SINGLE_VEHICLE_COLLISION_TARGET_IDS.has(o.singleVehicleCollisionTarget as SingleVehicleCollisionTargetId)) {
      return false
    }
  }
  if (
    o.singleVehicleCollisionTargetOtherText !== undefined &&
    o.singleVehicleCollisionTargetOtherText !== null &&
    typeof o.singleVehicleCollisionTargetOtherText !== 'string'
  ) {
    return false
  }
  if (
    o.singleVehicleIncidentDescription !== undefined &&
    o.singleVehicleIncidentDescription !== null &&
    typeof o.singleVehicleIncidentDescription !== 'string'
  ) {
    return false
  }
  if (
    o.singleVehicleDamageDescription !== undefined &&
    o.singleVehicleDamageDescription !== null &&
    typeof o.singleVehicleDamageDescription !== 'string'
  ) {
    return false
  }
  return true
}

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

function isValidMultiVehicleDobDdMmYyyy(s: string): boolean {
  const t = s.trim()
  return isDdMmYyyyInLocalRange(t, PORTAL_DATE_MIN, startOfTodayLocal())
}

function isValidMultiVehicleLicenseExpiryDdMmYyyy(s: string): boolean {
  const raw = s.trim()
  if (!isValidDdMmYyyyCalendarDate(raw)) return false
  const d = parseDdMmYyyyToDate(raw)
  if (!d) return false
  return startOfLocalDay(d) >= startOfLocalDay(startOfTodayLocal())
}

function isMultiVehicleIdentityAndLicenseComplete(draft: ClaimFlowDraft): boolean {
  const fn = draft.multiVehicleDriverFirstName?.trim()
  const ln = draft.multiVehicleDriverLastName?.trim()
  const dob = draft.multiVehicleDriverDobDdMmYyyy?.trim()
  const lic = draft.multiVehicleLicenseNumber?.trim()
  const exp = draft.multiVehicleLicenseExpiryDdMmYyyy?.trim()
  const years = draft.multiVehicleYearsLicensed
  if (!fn || !ln || !dob || !isValidMultiVehicleDobDdMmYyyy(dob)) return false
  if (!lic || !exp || !isValidMultiVehicleLicenseExpiryDdMmYyyy(exp)) return false
  if (typeof years !== 'number' || !Number.isInteger(years) || years < 1 || years > 100) return false
  return true
}

/** Draft keys cleared when `otherClaimSubtypeId` changes so driver-capture data does not leak across subtypes. */
const MULTI_VEHICLE_FLOW_CLEAR_KEYS: (keyof ClaimFlowDraft)[] = [
  'multiVehicleAttendedAtCollision',
  'multiVehiclePrimaryDriverDriving',
  'multiVehicleSecondaryOnPolicyDriving',
  'multiVehicleListedDriverId',
  'multiVehicleDriverCaptureMode',
  'multiVehicleDriverFirstName',
  'multiVehicleDriverLastName',
  'multiVehicleDriverDobDdMmYyyy',
  'multiVehicleDriverGender',
  'multiVehicleLicenseNumber',
  'multiVehicleLicenseExpiryDdMmYyyy',
  'multiVehicleYearsLicensed',
  'multiVehicleOtherPartyDetailsKnown',
  'multiVehicleOtherPartyName',
  'multiVehicleOtherPartyAddress',
  'multiVehicleOtherPartyDobDdMmYyyy',
  'multiVehicleOtherPartyPhone',
  'multiVehicleOtherPartyRegistration',
  'multiVehicleOtherPartyLicenceNumber',
  'multiVehicleOtherParty2Name',
  'multiVehicleOtherParty2Address',
  'multiVehicleOtherParty2Phone',
  'multiVehicleOtherParty2Registration',
  'multiVehicleOtherParty3Name',
  'multiVehicleOtherParty3Address',
  'multiVehicleOtherParty3Phone',
  'multiVehicleOtherParty3Registration',
  'multiVehicleClaimDescription',
  'weatherClaimConditionsDescription',
  'fireClaimTypeDescription',
  'fireVehicleParkedAtIncident',
  'fireVehicleStillParkedAtLocation',
  'weatherVehicleParkedAtIncident',
  'weatherVehicleStillParkedAtLocation',
  'theftOrBreakInDetailDescription',
  'theftVehicleParkedAtIncident',
  'theftVehicleStolenAtIncident',
  'theftItemsStolenFromVehicle',
  'theftSomethingStolenAtIncident',
  'theftVehicleStillParkedAtLocation',
  'multiVehicleIncidentDescription',
  'multiVehicleDamageDescription',
  'singleVehicleCollisionTarget',
  'singleVehicleCollisionTargetOtherText',
  'singleVehicleIncidentDescription',
  'singleVehicleDamageDescription',
]

function clearMultiVehicleFlowSliceFromSession(): void {
  try {
    sessionStorage.removeItem(CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY)
  } catch {
    // ignore
  }
}

/**
 * True when insured-driver capture (shared draft shape) is complete for multi-vehicle or single-vehicle collision.
 * Multi-vehicle unattended uses {@link multiVehicleDriverCaptureMode} `none_unattended`; single-vehicle never does.
 */
export function isOtherClaimDriverCaptureGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (!draft) return false
  const id = draft.otherClaimSubtypeId
  if (
    id !== 'multi-vehicle-collision' &&
    id !== 'single-vehicle' &&
    id !== 'weather' &&
    id !== 'fire' &&
    id !== 'theft'
  ) {
    return false
  }
  const mode = draft.multiVehicleDriverCaptureMode
  if (mode === 'none_unattended') {
    return false
  }
  if (mode === null || mode === undefined) return false
  if (mode === 'primary' || mode === 'listed' || mode === 'manual') {
    return isMultiVehicleIdentityAndLicenseComplete(draft)
  }
  return false
}

/**
 * True when the multi-vehicle driver step has been completed in a way that allows “further details”.
 */
export function isMultiVehicleCollisionDetailsGateSatisfied(
  draft: ClaimFlowDraft | null | undefined
): boolean {
  return draft?.otherClaimSubtypeId === 'multi-vehicle-collision' && isOtherClaimDriverCaptureGateSatisfied(draft)
}

/**
 * True when the single-vehicle driver-at-collision step is complete (before “what you collided with”).
 */
export function isSingleVehicleDriverDetailsGateSatisfied(
  draft: ClaimFlowDraft | null | undefined
): boolean {
  return draft?.otherClaimSubtypeId === 'single-vehicle' && isOtherClaimDriverCaptureGateSatisfied(draft)
}

/**
 * True when the weather-claim driver step is complete: either insured driver captured (not parked) or parked
 * at incident (no driver questions), mirroring {@link isFireDriverDetailsGateSatisfied}.
 */
export function isWeatherDriverDetailsGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (draft?.otherClaimSubtypeId !== 'weather') return false
  if (isWeatherParkedLocationGateSatisfied(draft)) return true
  return isOtherClaimDriverCaptureGateSatisfied(draft)
}

/**
 * Fire claims only: vehicle was parked at incident (driver step skipped; no parked-address capture).
 */
export function isFireParkedLocationGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'fire') return false
  return draft.fireVehicleParkedAtIncident === true
}

/**
 * Weather claims only: same parked path rules as {@link isFireParkedLocationGateSatisfied}.
 */
export function isWeatherParkedLocationGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'weather') return false
  return draft.weatherVehicleParkedAtIncident === true
}

/**
 * True when the fire-claim driver step is complete: either insured driver captured (not parked) or parked at
 * incident (no driver questions).
 */
export function isFireDriverDetailsGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (draft?.otherClaimSubtypeId !== 'fire') return false
  if (isFireParkedLocationGateSatisfied(draft)) return true
  return isOtherClaimDriverCaptureGateSatisfied(draft)
}

/**
 * Theft claims only: vehicle was parked at incident (driver step skipped; no parked-address capture).
 */
export function isTheftParkedLocationGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'theft') return false
  return draft.theftVehicleParkedAtIncident === true
}

/** Theft claims only: was the whole vehicle stolen (null if unanswered). */
export function resolveTheftVehicleStolenAtIncident(
  draft: ClaimFlowDraft | null | undefined
): boolean | null {
  if (!draft) return null
  if (typeof draft.theftVehicleStolenAtIncident === 'boolean') {
    return draft.theftVehicleStolenAtIncident
  }
  return null
}

/**
 * Theft claims only: items stolen from vehicle when it was not stolen (null if unanswered or N/A).
 * Falls back to legacy {@link theftSomethingStolenAtIncident} when the new vehicle-stolen field is unset.
 */
export function resolveTheftItemsStolenFromVehicle(
  draft: ClaimFlowDraft | null | undefined
): boolean | null {
  if (!draft) return null
  if (resolveTheftVehicleStolenAtIncident(draft) === true) return null
  if (typeof draft.theftItemsStolenFromVehicle === 'boolean') {
    return draft.theftItemsStolenFromVehicle
  }
  if (typeof draft.theftSomethingStolenAtIncident === 'boolean') {
    return draft.theftSomethingStolenAtIncident
  }
  return null
}

export function isTheftWholeVehicleStolen(draft: ClaimFlowDraft | null | undefined): boolean {
  return resolveTheftVehicleStolenAtIncident(draft) === true
}

/**
 * Damage field for theft FNOL submit. Empty when the whole vehicle was stolen (no damage narrative for Entsia).
 */
export function theftDamageDescriptionForSubmit(
  draft: ClaimFlowDraft | null | undefined,
  damageText: string
): string {
  if (isTheftWholeVehicleStolen(draft)) return ''
  return damageText.trim()
}

/**
 * Theft describe step: damage narrative required unless the whole vehicle was stolen.
 */
export function isTheftDescribeDamageRequired(draft: ClaimFlowDraft | null | undefined): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'theft') return true
  return !isTheftWholeVehicleStolen(draft)
}

/**
 * Theft claims only: parked and vehicle/items stolen questions on the vehicle-at-incident screen are answered.
 */
export function isTheftVehicleAtIncidentScreenComplete(
  draft: ClaimFlowDraft | null | undefined
): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'theft') return false
  const parked = draft.theftVehicleParkedAtIncident
  if (parked !== true && parked !== false) return false

  const vehicleStolen = resolveTheftVehicleStolenAtIncident(draft)
  if (vehicleStolen === null) {
    return typeof draft.theftSomethingStolenAtIncident === 'boolean'
  }
  if (vehicleStolen === true) return true

  const items = resolveTheftItemsStolenFromVehicle(draft)
  return items === true || items === false
}

/**
 * True when the theft-claim driver step is complete: either insured driver captured (not parked) or parked at
 * incident (no driver questions).
 */
export function isTheftDriverDetailsGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (draft?.otherClaimSubtypeId !== 'theft') return false
  if (!isTheftVehicleAtIncidentScreenComplete(draft)) return false
  if (isTheftParkedLocationGateSatisfied(draft)) return true
  return isOtherClaimDriverCaptureGateSatisfied(draft)
}

const SINGLE_VEHICLE_COLLISION_TARGET_IDS = new Set<SingleVehicleCollisionTargetId>([
  'animal',
  'object',
  'third_party_property',
  'other',
])

/**
 * True when the user has selected what the vehicle collided with (and “other” includes non-empty text).
 */
export function isSingleVehicleCollisionTargetGateSatisfied(
  draft: ClaimFlowDraft | null | undefined
): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'single-vehicle') return false
  const t = draft.singleVehicleCollisionTarget
  if (t === null || t === undefined || !SINGLE_VEHICLE_COLLISION_TARGET_IDS.has(t)) return false
  if (t === 'other') {
    return Boolean(draft.singleVehicleCollisionTargetOtherText?.trim())
  }
  return true
}

/**
 * True after the user answers whether they have details for the other driver (multi-vehicle collision only).
 */
export function isMultiVehicleOtherPartyQuestionAnswered(
  draft: ClaimFlowDraft | null | undefined
): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'multi-vehicle-collision') return true
  const v = draft.multiVehicleOtherPartyDetailsKnown
  return v === true || v === false
}

/**
 * Incident and damage text used for gates, review, and submission (legacy single field maps to incident only).
 */
export function getMultiVehicleDescribeParts(draft: ClaimFlowDraft | null | undefined): {
  incident: string
  damage: string
} {
  const legacy = draft?.multiVehicleClaimDescription?.trim() ?? ''
  const incident = draft?.multiVehicleIncidentDescription?.trim() || legacy
  const damage = draft?.multiVehicleDamageDescription?.trim() ?? ''
  return { incident, damage }
}

const MV_DESCRIBE_FIELD_MAX = 1000

/**
 * When {@link ClaimFlowDraft.multiVehicleIncidentDescription} is empty but legacy
 * {@link ClaimFlowDraft.multiVehicleClaimDescription} has text, copy into incident so raw draft readers
 * (e.g. progress rail props) match {@link getMultiVehicleDescribeParts}.
 */
function normalizeLegacyMultiVehicleIncidentInDraft(merged: ClaimFlowDraft): {
  merged: ClaimFlowDraft
  didNormalize: boolean
} {
  if (merged.otherClaimSubtypeId !== 'multi-vehicle-collision') {
    return { merged, didNormalize: false }
  }
  const legacy = merged.multiVehicleClaimDescription?.trim() ?? ''
  if (!legacy) return { merged, didNormalize: false }
  if ((merged.multiVehicleIncidentDescription ?? '').trim() !== '') {
    return { merged, didNormalize: false }
  }
  return {
    merged: {
      ...merged,
      multiVehicleIncidentDescription: legacy.slice(0, MV_DESCRIBE_FIELD_MAX),
    },
    didNormalize: true,
  }
}

/**
 * True when the user has entered non-empty incident and damage narratives (before confirm step).
 */
export function isMultiVehicleDescribeGateSatisfied(
  draft: ClaimFlowDraft | null | undefined
): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'multi-vehicle-collision') return false
  const { incident, damage } = getMultiVehicleDescribeParts(draft)
  return Boolean(incident) && Boolean(damage)
}

/**
 * Incident and damage text for single-vehicle describe, review, and submission.
 */
export function getSingleVehicleDescribeParts(draft: ClaimFlowDraft | null | undefined): {
  incident: string
  damage: string
} {
  const incident = draft?.singleVehicleIncidentDescription?.trim() ?? ''
  const damage = draft?.singleVehicleDamageDescription?.trim() ?? ''
  return { incident, damage }
}

const SV_DESCRIBE_FIELD_MAX = 1000

/**
 * True when the user has entered non-empty incident and damage narratives (single-vehicle, before review).
 */
export function isSingleVehicleDescribeGateSatisfied(
  draft: ClaimFlowDraft | null | undefined
): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'single-vehicle') return false
  const { incident, damage } = getSingleVehicleDescribeParts(draft)
  return Boolean(incident) && Boolean(damage)
}

/**
 * True when weather narrative and weather-conditions text are both non-empty.
 */
export function isWeatherDescribeGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'weather') return false
  const { incident, damage } = getMultiVehicleDescribeParts(draft)
  return Boolean(incident) && Boolean(damage)
}

/**
 * True when fire incident and damage narratives are both non-empty.
 */
export function isFireDescribeGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'fire') return false
  const { incident, damage } = getMultiVehicleDescribeParts(draft)
  return Boolean(incident) && Boolean(damage)
}

/**
 * True when theft describe narratives are complete (damage omitted when the whole vehicle was stolen).
 */
export function isTheftDescribeGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'theft') return false
  const { incident, damage } = getMultiVehicleDescribeParts(draft)
  if (!incident) return false
  if (!isTheftDescribeDamageRequired(draft)) return true
  return Boolean(damage)
}

/** True when malicious-damage incident and damage narratives are both non-empty. */
export function isMaliciousDamageDescribeGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'malicious-damage') return false
  const { incident, damage } = getMultiVehicleDescribeParts(draft)
  return Boolean(incident) && Boolean(damage)
}

/** True when other-loss incident and damage narratives are both non-empty. */
export function isOtherLossDescribeGateSatisfied(draft: ClaimFlowDraft | null | undefined): boolean {
  if (!draft || draft.otherClaimSubtypeId !== 'other-loss') return false
  const { incident, damage } = getMultiVehicleDescribeParts(draft)
  return Boolean(incident) && Boolean(damage)
}

/**
 * Merge {@link readDraftStorageRaw} with {@link readClaimDraft} so persist patches never drop keys when
 * validation briefly returns null (see module note on {@link readDraftStorageRaw}). When validation
 * succeeds, use the validated snapshot only so raw storage type drift cannot overwrite good fields.
 */
function mergeClaimDraftPersistBase(): ClaimFlowDraft {
  const prev = readClaimDraft()
  if (prev) return prev
  return readDraftStorageRaw() as unknown as ClaimFlowDraft
}

/**
 * Persist single-vehicle incident and damage narratives (debounced by the caller). Truncates each field.
 */
export function persistSingleVehicleDescribeNarratives(
  policyRef: string | null,
  incident: string,
  damage: string
): void {
  try {
    const base = mergeClaimDraftPersistBase()
    const payload: ClaimFlowDraft = {
      ...base,
      policyRef: policyRef ?? null,
      singleVehicleIncidentDescription: incident.slice(0, SV_DESCRIBE_FIELD_MAX),
      singleVehicleDamageDescription: damage.slice(0, SV_DESCRIBE_FIELD_MAX),
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist single-vehicle describe narratives', err)
  }
}

/**
 * Persist multi-vehicle “what happened” narrative for weather FNOL ({@link ClaimFlowDraft.multiVehicleClaimDescription}). Debounced by callers.
 */
export function persistMultiVehicleClaimDescription(policyRef: string | null, description: string): void {
  try {
    const base = mergeClaimDraftPersistBase()
    const trimmed = description.slice(0, 1000)
    const payload: ClaimFlowDraft = {
      ...base,
      policyRef: policyRef ?? null,
      multiVehicleClaimDescription: trimmed,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist multi-vehicle claim description', err)
  }
}

/**
 * Persist weather-conditions narrative for the weather FNOL branch (debounced by callers).
 */
export function persistWeatherClaimConditionsDescription(policyRef: string | null, text: string): void {
  try {
    const base = mergeClaimDraftPersistBase()
    const trimmed = text.slice(0, 1000)
    const payload: ClaimFlowDraft = {
      ...base,
      policyRef: policyRef ?? null,
      weatherClaimConditionsDescription: trimmed,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist weather claim conditions description', err)
  }
}

/**
 * Persist “type of fire” text for the fire FNOL branch (debounced by callers).
 */
export function persistFireClaimTypeDescription(policyRef: string | null, text: string): void {
  try {
    const base = mergeClaimDraftPersistBase()
    const trimmed = text.slice(0, 1000)
    const payload: ClaimFlowDraft = {
      ...base,
      policyRef: policyRef ?? null,
      fireClaimTypeDescription: trimmed,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist fire claim type description', err)
  }
}

/**
 * Persist theft / break-in detail text for the theft FNOL branch (debounced by callers).
 */
export function persistTheftOrBreakInDetailDescription(policyRef: string | null, text: string): void {
  try {
    const base = mergeClaimDraftPersistBase()
    const trimmed = text.slice(0, 1000)
    const payload: ClaimFlowDraft = {
      ...base,
      policyRef: policyRef ?? null,
      theftOrBreakInDetailDescription: trimmed,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist theft or break-in detail description', err)
  }
}

/**
 * Persist multi-vehicle incident and damage narratives (debounced by the caller). Truncates each field.
 * Clears {@link ClaimFlowDraft.multiVehicleClaimDescription} so the legacy key does not duplicate the incident body.
 */
export function persistMultiVehicleDescribeNarratives(
  policyRef: string | null,
  incident: string,
  damage: string
): void {
  try {
    const base = mergeClaimDraftPersistBase()
    const payload: ClaimFlowDraft = {
      ...base,
      policyRef: policyRef ?? null,
      multiVehicleIncidentDescription: incident.slice(0, MV_DESCRIBE_FIELD_MAX),
      multiVehicleDamageDescription: damage.slice(0, MV_DESCRIBE_FIELD_MAX),
      multiVehicleClaimDescription: null,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist multi-vehicle describe narratives', err)
  }
}

/**
 * Persist single-vehicle “what did you collide with?” answers (after driver step).
 */
export function persistSingleVehicleCollisionTargetPatch(
  policyRef: string | null,
  patch: Partial<Pick<ClaimFlowDraft, 'singleVehicleCollisionTarget' | 'singleVehicleCollisionTargetOtherText'>>
): void {
  try {
    const payload: ClaimFlowDraft = {
      ...mergeClaimDraftPersistBase(),
      policyRef: policyRef ?? null,
      ...patch,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist single-vehicle collision target draft', err)
  }
}

/**
 * Clears driver identity/licence captured on the driver step so a new primary/listed/manual choice can re-prefill.
 */
export function multiVehicleDriverIdentityResetPatch(): Partial<
  Pick<
    ClaimFlowDraft,
    | 'multiVehicleDriverCaptureMode'
    | 'multiVehicleDriverFirstName'
    | 'multiVehicleDriverLastName'
    | 'multiVehicleDriverDobDdMmYyyy'
    | 'multiVehicleDriverGender'
    | 'multiVehicleLicenseNumber'
    | 'multiVehicleLicenseExpiryDdMmYyyy'
    | 'multiVehicleYearsLicensed'
  >
> {
  return {
    multiVehicleDriverCaptureMode: null,
    multiVehicleDriverFirstName: null,
    multiVehicleDriverLastName: null,
    multiVehicleDriverDobDdMmYyyy: null,
    multiVehicleDriverGender: null,
    multiVehicleLicenseNumber: null,
    multiVehicleLicenseExpiryDdMmYyyy: null,
    multiVehicleYearsLicensed: null,
  }
}

export function persistMultiVehicleCollisionPatch(
  policyRef: string | null,
  patch: Partial<
    Pick<
      ClaimFlowDraft,
      | 'multiVehicleAttendedAtCollision'
      | 'multiVehiclePrimaryDriverDriving'
      | 'multiVehicleSecondaryOnPolicyDriving'
      | 'multiVehicleListedDriverId'
      | 'multiVehicleDriverCaptureMode'
      | 'multiVehicleDriverFirstName'
      | 'multiVehicleDriverLastName'
      | 'multiVehicleDriverDobDdMmYyyy'
      | 'multiVehicleDriverGender'
      | 'multiVehicleLicenseNumber'
      | 'multiVehicleLicenseExpiryDdMmYyyy'
      | 'multiVehicleYearsLicensed'
      | 'fireVehicleParkedAtIncident'
      | 'fireVehicleStillParkedAtLocation'
      | 'weatherVehicleParkedAtIncident'
      | 'weatherVehicleStillParkedAtLocation'
      | 'theftVehicleParkedAtIncident'
      | 'theftVehicleStolenAtIncident'
      | 'theftItemsStolenFromVehicle'
      | 'theftSomethingStolenAtIncident'
      | 'theftVehicleStillParkedAtLocation'
    >
  >
): void {
  try {
    const payload: ClaimFlowDraft = {
      ...mergeClaimDraftPersistBase(),
      policyRef: policyRef ?? null,
      ...patch,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist multi-vehicle collision draft', err)
  }
}

/**
 * Persist answers for the other involved driver (multi-vehicle collision), including optional contact fields.
 */
export function persistMultiVehicleOtherPartyPatch(
  policyRef: string | null,
  patch: Partial<
    Pick<
      ClaimFlowDraft,
      | 'multiVehicleOtherPartyDetailsKnown'
      | 'multiVehicleOtherPartyName'
      | 'multiVehicleOtherPartyAddress'
      | 'multiVehicleOtherPartyDobDdMmYyyy'
      | 'multiVehicleOtherPartyPhone'
      | 'multiVehicleOtherPartyRegistration'
      | 'multiVehicleOtherPartyLicenceNumber'
      | 'multiVehicleOtherParty2Name'
      | 'multiVehicleOtherParty2Address'
      | 'multiVehicleOtherParty2Phone'
      | 'multiVehicleOtherParty2Registration'
      | 'multiVehicleOtherParty3Name'
      | 'multiVehicleOtherParty3Address'
      | 'multiVehicleOtherParty3Phone'
      | 'multiVehicleOtherParty3Registration'
    >
  >
): void {
  try {
    const payload: ClaimFlowDraft = {
      ...mergeClaimDraftPersistBase(),
      policyRef: policyRef ?? null,
      ...patch,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist multi-vehicle other-party draft', err)
  }
}

/**
 * Clears any persisted claim funnel draft ({@link CLAIM_FLOW_DRAFT_STORAGE_KEY}) for this tab.
 * Call when the user intentionally begins a new claim (e.g. from the claims hub) so prior answers are not reused.
 */
export function clearClaimDraft(): void {
  clearDraftStorage()
}

/** Read persisted draft JSON from storage; returns `null` if missing, invalid JSON, or invalid shape. */
export function readClaimDraft(): ClaimFlowDraft | null {
  try {
    const raw = sessionStorage.getItem(CLAIM_FLOW_DRAFT_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    const { value: normalizedParsed, didRewriteSubtype } = normalizeLegacyOtherClaimSubtypeDraftShape(parsed)
    if (!isClaimFlowDraft(normalizedParsed)) {
      console.warn(
        '[claims] Draft in sessionStorage failed validation; leaving storage intact so persist can still merge.'
      )
      return null
    }
    const draft = migrateLegacyMvDriverPiiOutOfMainIfPresent(normalizedParsed as ClaimFlowDraft)
    const pii = readMvDriverPiiRecord()
    let merged: ClaimFlowDraft = pii ? mergeMvPiiRecordIntoDraft(draft, pii) : draft

    /** Legacy field; portal no longer collects ABN — strip from storage and return value. */
    if (merged.windscreenAbn != null) {
      merged = { ...merged, windscreenAbn: null }
      writeClaimFlowDraftToStorage(merged)
    }

    const mvLegacy = normalizeLegacyMultiVehicleIncidentInDraft(merged)
    if (mvLegacy.didNormalize) {
      merged = mvLegacy.merged
      writeClaimFlowDraftToStorage(merged)
    }

    if (didRewriteSubtype) {
      writeClaimFlowDraftToStorage(merged)
    }

    const unattendedMigration = migrateLegacyMultiVehicleUnattendedDraft(merged)
    if (unattendedMigration.didMigrate) {
      merged = unattendedMigration.merged
      writeClaimFlowDraftToStorage(merged)
      sessionStorage.removeItem(CLAIM_FLOW_MV_DRIVER_PII_STORAGE_KEY)
    }

    return merged
  } catch {
    clearDraftStorage()
    return null
  }
}

/**
 * Persists towing requirement for the current claim draft. Sync only; failures are logged and swallowed
 * so navigation can still proceed (see {@link ClaimTowingQuestion}).
 */
export function persistClaimTowingAnswer(policyRef: string | null, requiresTowing: boolean): void {
  try {
    const base = readDraftStorageRaw()
    const payload: Record<string, unknown> = {
      ...base,
      policyRef: policyRef ?? null,
      requiresTowing,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist claim towing answer', err)
  }
}

/**
 * Persists windscreen/glass incident description (debounced by the caller). Truncates to 1000 chars.
 */
export function persistWindscreenDescription(policyRef: string | null, description: string): void {
  try {
    const base = readDraftStorageRaw()
    const trimmed = description.slice(0, 1000)
    const payload: Record<string, unknown> = {
      ...base,
      policyRef: policyRef ?? null,
      windscreenDescription: trimmed,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist windscreen description', err)
  }
}

/**
 * Persists whether windscreen damage is a small chip (windscreen/glass branch).
 */
export function persistWindscreenSmallChip(policyRef: string | null, isSmallChip: boolean): void {
  try {
    const base = readDraftStorageRaw()
    const payload: Record<string, unknown> = {
      ...base,
      policyRef: policyRef ?? null,
      windscreenSmallChip: isSmallChip,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist windscreen small-chip answer', err)
  }
}

/**
 * Persists whether the windscreen was already replaced or repaired before lodgement.
 * When `true`, clears repair option and any repair-option repairer fields (prior repairer is captured separately).
 * When `false`, clears prior-repairer contact so the repair-option step starts clean.
 */
export function persistWindscreenReplacedAlready(policyRef: string | null, replacedAlready: boolean | null): void {
  try {
    const base = readDraftStorageRaw()
    const payload: Record<string, unknown> = {
      ...base,
      policyRef: policyRef ?? null,
      windscreenReplacedAlready: replacedAlready,
      updatedAt: new Date().toISOString(),
    }
    if (replacedAlready === true) {
      payload.windscreenRepairOption = null
      payload.windscreenRepairerCompany = null
      payload.windscreenRepairerEmail = null
      payload.windscreenRepairerPhone = null
    }
    if (replacedAlready === false || replacedAlready === null) {
      payload.windscreenRepairerCompany = null
      payload.windscreenRepairerEmail = null
      payload.windscreenRepairerPhone = null
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist windscreen replaced-already answer', err)
  }
}

/**
 * Persists repairer/business contact for a windscreen already repaired (further-detail step; no repair option yet).
 */
export function persistWindscreenCompletedRepairerDetails(
  policyRef: string | null,
  data: { repairerCompany: string; repairerEmail: string; repairerPhone: string }
): void {
  try {
    const base = readDraftStorageRaw()
    const payload: Record<string, unknown> = {
      ...base,
      policyRef: policyRef ?? null,
      windscreenRepairerCompany: data.repairerCompany.trim() || null,
      windscreenRepairerEmail: data.repairerEmail.trim() || null,
      windscreenRepairerPhone: data.repairerPhone.trim() || null,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist completed repairer details', err)
  }
}

/**
 * Persists windscreen repair preference and optional own-repairer contact fields.
 */
export function persistWindscreenRepair(
  policyRef: string | null,
  data: {
    option: 'trusted' | 'own'
    repairerCompany?: string
    repairerEmail?: string
    repairerPhone?: string
  }
): void {
  try {
    const base = readDraftStorageRaw()
    const payload: Record<string, unknown> = {
      ...base,
      policyRef: policyRef ?? null,
      windscreenRepairOption: data.option,
      windscreenRepairerCompany: data.repairerCompany?.trim() || null,
      windscreenRepairerEmail: data.repairerEmail?.trim() || null,
      windscreenRepairerPhone: data.repairerPhone?.trim() || null,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist windscreen repair preference', err)
  }
}

/**
 * Stores the chosen non–windscreen claim subtype (collision, theft, etc.).
 */
export function persistOtherClaimSubtype(policyRef: string | null, subtypeId: string): void {
  if (!ALLOWED_OTHER_SUBTYPE_IDS.has(subtypeId)) {
    console.warn('persistOtherClaimSubtype: ignoring unknown subtype id', subtypeId)
    return
  }
  try {
    const base = readDraftStorageRaw()
    const prevSubtype = typeof base.otherClaimSubtypeId === 'string' ? base.otherClaimSubtypeId : null
    const payload: Record<string, unknown> = {
      ...base,
      policyRef: policyRef ?? null,
      otherClaimSubtypeId: subtypeId,
      updatedAt: new Date().toISOString(),
    }
    if (prevSubtype !== null && prevSubtype !== subtypeId) {
      for (const k of MULTI_VEHICLE_FLOW_CLEAR_KEYS) {
        payload[k] = null
      }
      clearMultiVehicleFlowSliceFromSession()
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist other claim subtype', err)
  }
}

/** Persists incident date/time and location fields for the claim funnel. */
export function persistIncidentSnapshot(
  policyRef: string | null,
  patch: Partial<
    Pick<
      ClaimFlowDraft,
      | 'incidentDateDdMmYyyy'
      | 'incidentTimeHm'
      | 'incidentTimeAmPm'
      | 'incidentAddressSearch'
      | 'incidentStreet'
      | 'incidentSuburb'
      | 'incidentPostcode'
      | 'incidentState'
      | 'incidentAddressUnknown'
    >
  >
): void {
  try {
    const base = readDraftStorageRaw()
    const payload: Record<string, unknown> = {
      ...base,
      policyRef: policyRef ?? null,
      ...patch,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist incident snapshot', err)
  }
}

/**
 * Persists GST registration answer and conditional ITC answers (windscreen branch, before describe).
 * ABN is not collected in the portal; stored value is always cleared.
 */
export function persistWindscreenGst(
  policyRef: string | null,
  data: {
    gstRegistered: boolean
    /** When not GST-registered, pass `null` to clear ITC fields. */
    itcEntitled: boolean | null
    /** 0–100 when GST + ITC yes; otherwise `null`. */
    itcPercentage: number | null
  }
): void {
  try {
    const base = mergeClaimDraftPersistBase()
    const gst = data.gstRegistered
    const itcEntitled = gst ? data.itcEntitled : null
    const itcPercentage =
      gst && itcEntitled === true && typeof data.itcPercentage === 'number' ? data.itcPercentage : null
    const payload: ClaimFlowDraft = {
      ...base,
      policyRef: policyRef ?? null,
      windscreenGstRegistered: gst,
      windscreenAbn: null,
      windscreenItcEntitled: itcEntitled,
      windscreenItcPercentage: itcPercentage,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist windscreen GST', err)
  }
}

/**
 * Persists attachment file names from the further-detail step (files themselves are not stored).
 */
export function persistWindscreenAttachmentFileNames(policyRef: string | null, fileNames: string[]): void {
  try {
    const base = readDraftStorageRaw()
    const capped = fileNames.slice(0, 20).map((n) => n.slice(0, 240))
    const payload: Record<string, unknown> = {
      ...base,
      policyRef: policyRef ?? null,
      windscreenAttachmentFileNames: capped,
      updatedAt: new Date().toISOString(),
    }
    writeClaimFlowDraftToStorage(payload)
  } catch (err) {
    console.error('Failed to persist windscreen attachment file names', err)
  }
}
